import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormControlName,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialogConfig,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  AgencyStore,
  AppState,
  ItemStore,
  SchemaEditorActions,
  selectSchemaViewBySid,
  StepperDataPayload,
  StepperDataStore,
} from '@qtek/libs/store';
import { TemplateSelectorComponent } from '@qtek/libs/templates';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import {
  DocumentTemplate,
  Item,
  ItemData,
  ItemNdaWorkflow,
  ItemStage,
  ItemTypeBitMasks,
  NDA_WORKFLOW_OPTIONS,
  NdaWorkflow,
  ParticipationRole,
  ParticipationStage,
  TemplateTypes,
} from '@qtek/shared/models';
import { Observable, of } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  first,
  map,
  startWith,
  take,
} from 'rxjs/operators';
import { CcaExtractionResult } from '../closed-deal-entry-method-dialog/cca-extraction.utils';

export const closedDealStepperDialogConfig: MatDialogConfig = {
  panelClass: ['qt-dialog', 'closed-deal-stepper-dialog'],
  width: '800px',
  maxWidth: '95vw',
  maxHeight: '95vh',
};

export interface ClosedDealFormValue {
  setup: {
    hasSubFacilities: boolean;
    numSubFacilities: number;
    transactionType: string;
    dealName: string;
    numLenders: number;
    loanPurpose: string;
  };
  structure: {
    productType: string;
    rateType: string;
    amount: number;
    term: number;
    amortization: number | null;
    interestOnlyPeriod: number | null;
    closedDate: Date | null;
    maturityDate: Date | null;
  };
  borrower: {
    industry: string;
    revenueSize: string;
    peSponsored: string;
    usState: string;
  };
  pricing: {
    prepayment: string;
    expectedUtilization: string;
    fixedRate: number | null;
    spread: number | null;
    index: string;
    rateFloorType: string;
    floor: number | null;
    originationFee: number | null;
    performancePricing: string;
  };
  collateral: {
    recourseLevel: string;
    guarantorBurndown: string;
    hedge: string;
  };
  cre: {
    propertyClass: string;
    projectType: string;
    ltv: number | null;
    ltc: number | null;
    debtYield: number | null;
  };
  subFacilities: SubFacilityFormValue[];
  nda: {
    workflow: NdaWorkflow;
    template: DocumentTemplate | null;
  };
  _savedAt?: number;
}

export interface SubFacilityFormValue {
  // Inherited fields (editable per sub-facility in Review step)
  transactionType: string;
  dealName: string;
  numLenders: number;
  loanPurpose: string;
  industry: string;
  revenueSize: string;
  peSponsored: string;
  usState: string;
  recourseLevel: string;
  guarantorBurndown: string;
  // Sub-facility specific fields
  name: string;
  productType: string;
  rateType: string;
  amount: number | null;
  term: number | null;
  amortization: number | null;
  interestOnlyPeriod: number | null;
  closedDate: Date | null;
  maturityDate: Date | null;
  prepayment: string;
  expectedUtilization: string;
  fixedRate: number | null;
  index: string;
  spread: number | null;
  rateFloorType: string;
  floor: number | null;
}

interface SchemaEnumItem {
  id: string;
  label?: string;
  description?: string;
  name?: string;
}

const CRE_PRODUCT_TYPES_PATTERN = /construction/i;
const CRE_LOAN_PURPOSE_PATTERN = /^cre-/i;
const NO_AMORTIZATION_PATTERN = /line.of.credit|revolver|construction/i;
const STEPPER_NAME = 'closedDealStepper';
const DRAFT_TTL_MS = 30 * 60 * 1000; // 30 minutes
const CREATE_DEAL_MYSID = 'create_deal';

// Schema SID constants (mirrors backend SchemaService)
const SID_COMMON = 'io.synply.v1.model.core.common';
const SID_FEES = 'io.synply.v1.model.core.fees';
const SID_BORROWER = 'io.synply.v1.model.core.borrower';
const SID_COLLATERAL = 'io.synply.v1.model.core.collateral';
const SID_GUARANTORS = 'io.synply.v1.model.core.guarantors';
const SID_AGENCY = 'io.synply.v1.model.core.agency';
const SID_PARTICIPATION = 'io.synply.v1.model.derived.participation';
const SID_PRODUCT_TERM = 'io.synply.v1.model.product.term';

@Component({
  selector: 'qt-closed-deal-stepper-dialog',
  templateUrl: './closed-deal-stepper-dialog.component.html',
  styleUrls: ['./closed-deal-stepper-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [StepperDataStore],
  imports: [
    AsyncPipe,
    NgTemplateOutlet,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatRadioModule,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatDatepickerModule,
    MatNativeDateModule,
    TranslationCoreModule,
    TemplateSelectorComponent,
  ],
})
export class ClosedDealStepperDialogComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogRef = inject(
    MatDialogRef<ClosedDealStepperDialogComponent>
  );
  private readonly el = inject(ElementRef);
  private readonly itemStore = inject(ItemStore);
  private readonly stepperDataStore = inject(StepperDataStore);
  private readonly store = inject(Store<AppState>);
  private readonly agencyStore = inject(AgencyStore);
  private readonly router = inject(Router);

  /** Data passed from the entry method dialog (null when manual entry). */
  private readonly dialogData: {
    extractionResult?: CcaExtractionResult;
    // SD-2150: docId of the uploaded source doc, used to link it to the new deal.
    docId?: string;
  } | null = inject(MAT_DIALOG_DATA, { optional: true });

  /** Number of fields that were AI-extracted. */
  extractedFieldCount = signal(0);
  /** Set of "formGroup.fieldName" keys filled by AI extraction. */
  extractedFields = signal<Set<string>>(new Set());
  /** Whether to show the extraction info banner. */
  showExtractionBanner = signal(false);
  /** Gates eager red-outline reveal for extraction-blanked fields; manual entry never sets this. */
  revealExtractionGaps = signal(false);

  @ViewChild('stepper') stepper!: MatStepper;
  /** Feeds validateCurrentSubStep() the active sub-step's fields, derived from the template. */
  private readonly renderedControlNames = viewChildren(FormControlName);

  // All dropdown options loaded from backend schema
  productTypes$: Observable<SchemaEnumItem[]> = of([]);
  industries$: Observable<SchemaEnumItem[]> = of([]);
  usStates$: Observable<SchemaEnumItem[]> = of([]);
  syndicationTypes$: Observable<SchemaEnumItem[]> = of([]);
  loanPurposes$: Observable<SchemaEnumItem[]> = of([]);
  rateTypes$: Observable<SchemaEnumItem[]> = of([]);
  revenueSizes$: Observable<SchemaEnumItem[]> = of([]);
  rateIndices$: Observable<SchemaEnumItem[]> = of([]);
  rateFloorTypes$: Observable<SchemaEnumItem[]> = of([]);
  crePropertyClasses$: Observable<SchemaEnumItem[]> = of([]);
  creProjectTypes$: Observable<SchemaEnumItem[]> = of([]);
  prepaymentOptions$: Observable<SchemaEnumItem[]> = of([]);
  expectedUtilizationOptions$: Observable<SchemaEnumItem[]> = of([]);
  recourseLevelOptions$: Observable<SchemaEnumItem[]> = of([]);

  readonly subFacilityCountOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  readonly yesNoOptions = [
    { value: 'Yes', label: 'Yes' },
    { value: 'No', label: 'No' },
  ];

  readonly ndaWorkflowOptions = NDA_WORKFLOW_OPTIONS;

  readonly templateTypes = TemplateTypes;

  // ─── Form Groups ──────────────────────────────────────────

  setupForm = new FormGroup({
    hasSubFacilities: new FormControl<boolean>(false),
    numSubFacilities: new FormControl<number>(0),
    transactionType: new FormControl<string>('', Validators.required),
    dealName: new FormControl<string>('', Validators.required),
    numLenders: new FormControl<number>(1, [
      Validators.required,
      Validators.min(1),
    ]),
    loanPurpose: new FormControl<string>('', Validators.required),
  });

  structureForm = new FormGroup({
    productType: new FormControl<string>('', Validators.required),
    rateType: new FormControl<string>('', Validators.required),
    amount: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(0),
    ]),
    term: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(1),
    ]),
    amortization: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(0),
    ]),
    interestOnlyPeriod: new FormControl<number | null>(null),
    closedDate: new FormControl<Date | null>(null, Validators.required),
    maturityDate: new FormControl<Date | null>(null, Validators.required),
  });

  borrowerForm = new FormGroup({
    industry: new FormControl<string>('', Validators.required),
    revenueSize: new FormControl<string>('', Validators.required),
    peSponsored: new FormControl<string>('', Validators.required),
    usState: new FormControl<string>('', Validators.required),
  });

  pricingForm = new FormGroup({
    prepayment: new FormControl<string>('', Validators.required),
    expectedUtilization: new FormControl<string>(''),
    fixedRate: new FormControl<number | null>(null),
    spread: new FormControl<number | null>(null),
    index: new FormControl<string>(''),
    rateFloorType: new FormControl<string>(''),
    floor: new FormControl<number | null>(null),
    originationFee: new FormControl<number | null>(null, [Validators.min(0)]),
    performancePricing: new FormControl<string>('', Validators.required),
  });

  collateralForm = new FormGroup({
    recourseLevel: new FormControl<string>('', Validators.required),
    guarantorBurndown: new FormControl<string>('', Validators.required),
    hedge: new FormControl<string>(''),
  });

  creForm = new FormGroup({
    propertyClass: new FormControl<string>('', Validators.required),
    projectType: new FormControl<string>('', Validators.required),
    ltv: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(0),
      Validators.max(100),
    ]),
    ltc: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(0),
      Validators.max(100),
    ]),
    debtYield: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(0),
    ]),
  });

  ndaForm = new FormGroup({
    workflow: new FormControl<string>(NdaWorkflow.Personal, [
      Validators.required,
    ]),
    template: new FormControl<DocumentTemplate | null>(null),
  });

  // ─── Signals ──────────────────────────────────────────────

  isFloatingRate = signal(false);
  showCreStep = signal(false);
  showIOPeriod = signal(false);
  showAmortization = signal(true);
  showExpectedUtilization = signal(false);
  showFloorRate = signal(false);
  hasSubFacilities = signal(false);
  showNdaTemplates = signal(false);
  isSaving = signal(false);

  subFacilityForms = signal<FormGroup[]>([]);

  // Sub-stepper navigation
  currentMainStep = signal(0);
  currentSubFacility = signal(0);
  currentSubStep = signal(0); // 0=Review, 1=Structure, 2=Pricing
  subStepperEntered = signal(false);

  private stepperReady = false;
  private stepToRestore: number | null = null;
  private draftRestored = false;

  constructor() {
    effect(() => {
      const draft = this.stepperDataStore.draft();
      if (!draft || this.draftRestored) return;
      try {
        const data: Partial<ClosedDealFormValue> = JSON.parse(draft.jsonData);
        if (data._savedAt && Date.now() - data._savedAt > DRAFT_TTL_MS) {
          this.stepperDataStore.deleteDraft();
          return;
        }
        this.restoreForms(data);
        this.stepToRestore = draft.step;
      } catch {
        // ignore parse errors, start fresh
      }
      this.draftRestored = true;
      this.tryRestoreStep();
    });
  }

  // ─── Lifecycle ────────────────────────────────────────────

  ngAfterViewInit(): void {
    this.stepperReady = true;
    this.updateConnectorLines();
    this.tryRestoreStep();
  }

  ngOnDestroy(): void {
    this.agencyStore.disconnect(CREATE_DEAL_MYSID);
  }

  onStepChange(event: StepperSelectionEvent): void {
    this.currentMainStep.set(event.selectedIndex);
    this.updateConnectorLines(event.selectedIndex);
    this.saveDraft(event.selectedIndex);
  }

  get showSubStepper(): boolean {
    return this.subStepperEntered();
  }

  private updateConnectorLines(
    activeIndex = this.stepper?.selectedIndex ?? 0
  ): void {
    const dark = 'var(--app-main-text)';
    const gray = 'var(--app-frame-border)';

    const headers: HTMLElement[] = Array.from(
      this.el.nativeElement.querySelectorAll('.mat-horizontal-stepper-header')
    );
    headers.forEach((header, i) => {
      header.style.setProperty(
        '--qt-step-after-color',
        i < activeIndex ? dark : gray
      );
      header.style.setProperty(
        '--qt-step-before-color',
        i <= activeIndex ? dark : gray
      );
    });

    const lines: HTMLElement[] = Array.from(
      this.el.nativeElement.querySelectorAll('.mat-stepper-horizontal-line')
    );
    lines.forEach((line, i) => {
      line.style.setProperty(
        'border-top-color',
        i < activeIndex ? dark : gray,
        'important'
      );
    });
  }

  ngOnInit(): void {
    this.agencyStore.connect({
      op: 'sub',
      cmd: 'query',
      mysid: CREATE_DEAL_MYSID,
      view: 'agency',
    });

    this.loadSchemaOptions();

    // Rate type changes
    this.structureForm
      .get('rateType')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(val => {
        this.isFloatingRate.set(val === 'Floating');
        this.updatePricingValidators(val === 'Floating');
        this.updateHedgeVisibility(val === 'Floating');
      });

    // CRE visibility from loan purpose
    this.setupForm
      .get('loanPurpose')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateCreVisibility());

    // CRE visibility from product type
    this.structureForm
      .get('productType')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateCreVisibility();
        this.updateExpectedUtilizationVisibility();
        this.updateAmortizationVisibility();
      });

    // Rate floor type → floor rate visibility
    this.pricingForm
      .get('rateFloorType')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(val => {
        this.showFloorRate.set(!!val && val !== 'No Floor');
      });

    // Auto-calculate maturity date
    this.structureForm
      .get('closedDate')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.autoCalculateMaturityDate());
    this.structureForm
      .get('term')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.autoCalculateMaturityDate());

    // Sub-facilities toggle
    this.setupForm
      .get('hasSubFacilities')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(val => {
        this.hasSubFacilities.set(!!val);
        const numCtrl = this.setupForm.get('numSubFacilities');
        if (val) {
          numCtrl?.setValidators([Validators.required, Validators.min(1)]);
        } else {
          numCtrl?.clearValidators();
          numCtrl?.setValue(0);
          this.subFacilityForms.set([]);
          this.subStepperEntered.set(false);
        }
        numCtrl?.updateValueAndValidity();
      });

    // Sub-facilities count change
    this.setupForm
      .get('numSubFacilities')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(count => this.buildSubFacilityForms(count ?? 0));

    // When extraction data is present, skip draft restoration to avoid the
    // async draft response overwriting the freshly-patched extraction values.
    // Also reset all form groups to their initial state so a fresh upload
    // never inherits stale form values from the previous session's extraction
    // (e.g. dealName from the previously-uploaded CA). The backend draft is
    // kept intact to preserve the 30-min recovery window for the manual-entry
    // re-entry flow.
    if (this.dialogData?.extractionResult) {
      this.resetAllForms();
    } else {
      this.loadDraft();
    }
    this.applyExtractionData();

    this.dialogRef
      .beforeClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.isSaving()) {
          this.saveDraft(this.stepper?.selectedIndex ?? 0);
        }
      });

    // NDA workflow changes checks if the template field should be shown or hidden
    // and validates the template field accordingly
    this.ndaForm
      .get('workflow')
      .valueChanges.pipe(
        startWith(this.ndaForm.get('workflow').value),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(value => {
        const templateControl = this.ndaForm.get('template');

        if (value === NdaWorkflow.None) {
          templateControl.clearValidators();
          templateControl.setValue(null);
        } else if (value === NdaWorkflow.Personal) {
          templateControl.setValidators([Validators.required]);
        }

        this.showNdaTemplates.set(value === NdaWorkflow.Personal);
        this.ndaForm.updateValueAndValidity();
      });
  }

  // ─── Schema Loading ───────────────────────────────────────

  private loadSchemaOptions(): void {
    const schemaSids = [
      'io.synply.v1.type.product',
      'io.synply.v1.type.indu',
      'io.synply.v1.type.usState',
      'io.synply.v1.type.syndicationType',
      'io.synply.v1.type.loanPurpose',
      'io.synply.v1.type.interestRateType',
      'io.synply.v1.type.borrowerRevenueSize',
      'io.synply.v1.type.rateIndex',
      'io.synply.v1.type.interestRateFloorType',
      'io.synply.v1.type.crePropertyClass',
      'io.synply.v1.type.projectType',
      'io.synply.v1.type.prepaymentPenaltyFee',
      'io.synply.v1.type.expectedUtilization',
      'io.synply.v1.type.recourseLevel',
    ];
    schemaSids.forEach(sid =>
      this.store.dispatch(SchemaEditorActions.getSchema({ sid }))
    );

    const selectEnum = (sid: string) =>
      this.store.select(selectSchemaViewBySid(sid)).pipe(
        filter(schema => !!schema),
        map(({ graphEnum }) =>
          Object.entries(graphEnum.vals).map(([id, val]: [string, any]) => ({
            id,
            ...val,
          }))
        ),
        first()
      );

    this.productTypes$ = selectEnum('io.synply.v1.type.product');
    this.industries$ = selectEnum('io.synply.v1.type.indu');
    this.usStates$ = selectEnum('io.synply.v1.type.usState');
    this.syndicationTypes$ = selectEnum('io.synply.v1.type.syndicationType');
    this.loanPurposes$ = selectEnum('io.synply.v1.type.loanPurpose');
    this.rateTypes$ = selectEnum('io.synply.v1.type.interestRateType');
    this.revenueSizes$ = selectEnum('io.synply.v1.type.borrowerRevenueSize');
    this.rateIndices$ = selectEnum('io.synply.v1.type.rateIndex');
    this.rateFloorTypes$ = selectEnum(
      'io.synply.v1.type.interestRateFloorType'
    );
    this.crePropertyClasses$ = selectEnum('io.synply.v1.type.crePropertyClass');
    this.creProjectTypes$ = selectEnum('io.synply.v1.type.projectType');
    this.prepaymentOptions$ = selectEnum(
      'io.synply.v1.type.prepaymentPenaltyFee'
    );
    this.expectedUtilizationOptions$ = selectEnum(
      'io.synply.v1.type.expectedUtilization'
    );
    this.recourseLevelOptions$ = selectEnum('io.synply.v1.type.recourseLevel');
  }

  // ─── AI Extraction Pre-fill ────────────────────────────────

  private resetAllForms(): void {
    this.setupForm.reset({
      hasSubFacilities: false,
      numSubFacilities: 0,
      transactionType: '',
      dealName: '',
      numLenders: 1,
      loanPurpose: '',
    });
    this.structureForm.reset();
    this.borrowerForm.reset();
    this.pricingForm.reset();
    this.collateralForm.reset();
    this.creForm.reset();
    this.ndaForm.reset({ workflow: NdaWorkflow.Personal, template: null });
    this.subFacilityForms.set([]);
    this.hasSubFacilities.set(false);
    this.subStepperEntered.set(false);
    this.extractedFieldCount.set(0);
    this.extractedFields.set(new Set());
    this.showExtractionBanner.set(false);
  }

  private applyExtractionData(): void {
    const extraction = this.dialogData?.extractionResult;
    if (!extraction || extraction.filledFieldCount === 0) return;

    // Patch each form group with extracted values
    if (Object.keys(extraction.setup).length) {
      this.setupForm.patchValue(extraction.setup);
    }
    if (Object.keys(extraction.structure).length) {
      this.structureForm.patchValue(extraction.structure);
    }
    if (Object.keys(extraction.borrower).length) {
      this.borrowerForm.patchValue(extraction.borrower);
    }
    if (Object.keys(extraction.pricing).length) {
      this.pricingForm.patchValue(extraction.pricing);
    }
    if (Object.keys(extraction.collateral).length) {
      this.collateralForm.patchValue(extraction.collateral);
    }
    if (Object.keys(extraction.cre).length) {
      this.creForm.patchValue(extraction.cre);
    }

    // Apply sub-facility data if multiple facilities were extracted
    if (extraction.subFacilities?.length) {
      this.buildSubFacilityForms(extraction.subFacilities.length);
      const forms = this.subFacilityForms();
      extraction.subFacilities.forEach((sf, i) => {
        if (forms[i]) {
          forms[i].patchValue({
            ...sf,
            closedDate: sf.closedDate ? new Date(sf.closedDate as any) : null,
            maturityDate: sf.maturityDate
              ? new Date(sf.maturityDate as any)
              : null,
          });
        }
      });
    }

    this.extractedFieldCount.set(extraction.filledFieldCount);
    this.extractedFields.set(extraction.filledFields);
    this.showExtractionBanner.set(true);
    this.revealExtractionGaps.set(true);

    // Marking already-valid controls dirty is a visual no-op, so this is safe unconditionally.
    this.markFormsDirty(
      this.setupForm,
      this.structureForm,
      this.borrowerForm,
      this.pricingForm,
      this.collateralForm,
      this.creForm,
      ...this.subFacilityForms()
    );
  }

  private markFormsDirty(...forms: FormGroup[]): void {
    for (const form of forms) {
      form.markAllAsDirty();
      form.markAllAsTouched();
    }
  }

  /** Check if a specific field was AI-extracted (for visual indicators). */
  isExtractedField(formGroup: string, fieldName: string): boolean {
    return this.extractedFields().has(`${formGroup}.${fieldName}`);
  }

  dismissExtractionBanner(): void {
    this.showExtractionBanner.set(false);
  }

  // ─── Auto-calculate Maturity Date ─────────────────────────

  private autoCalculateMaturityDate(): void {
    const closedDate = this.structureForm.get('closedDate')?.value;
    const term = this.structureForm.get('term')?.value;
    if (closedDate && term) {
      const maturity = new Date(closedDate);
      maturity.setMonth(maturity.getMonth() + term);
      this.structureForm
        .get('maturityDate')
        ?.setValue(maturity, { emitEvent: false });
    }
  }

  // ─── Visibility Helpers ────────────────────────────────────

  private updateExpectedUtilizationVisibility(): void {
    const productType = this.structureForm.get('productType').value ?? '';
    const isTermLoan = /term/i.test(productType);
    this.showExpectedUtilization.set(!isTermLoan && !!productType);
  }

  private updateAmortizationVisibility(): void {
    const productType = this.structureForm.get('productType').value ?? '';
    const hide = NO_AMORTIZATION_PATTERN.test(productType);
    this.showAmortization.set(!hide);
    const ctrl = this.structureForm.get('amortization');
    if (!ctrl) return;
    if (hide) {
      ctrl.clearValidators();
      ctrl.setValue(null, { emitEvent: false });
    } else {
      ctrl.setValidators([Validators.required, Validators.min(0)]);
    }
    ctrl.updateValueAndValidity();
  }

  private updateHedgeVisibility(isFloating: boolean): void {
    const hedgeCtrl = this.collateralForm.get('hedge');
    if (isFloating) {
      hedgeCtrl.setValidators([Validators.required]);
    } else {
      hedgeCtrl.clearValidators();
      hedgeCtrl.setValue('');
    }
    hedgeCtrl.updateValueAndValidity();
  }

  // ─── Sub-facility Forms ───────────────────────────────────

  private buildSubFacilityForms(count: number): void {
    const existing = this.subFacilityForms();
    const forms: FormGroup[] = [];

    for (let i = 0; i < count; i++) {
      if (existing[i]) {
        forms.push(existing[i]);
      } else {
        forms.push(this.createSubFacilityForm());
      }
    }
    this.subFacilityForms.set(forms);
  }

  private createSubFacilityForm(): FormGroup {
    const setup = this.setupForm.getRawValue();
    const borrower = this.borrowerForm.getRawValue();
    const collateral = this.collateralForm.getRawValue();

    const form = new FormGroup({
      // Inherited fields (pre-filled from parent, editable)
      transactionType: new FormControl<string>(setup.transactionType),
      dealName: new FormControl<string>(setup.dealName),
      numLenders: new FormControl<number>(setup.numLenders),
      loanPurpose: new FormControl<string>(setup.loanPurpose),
      industry: new FormControl<string>(borrower.industry),
      revenueSize: new FormControl<string>(borrower.revenueSize),
      peSponsored: new FormControl<string>(borrower.peSponsored),
      usState: new FormControl<string>(borrower.usState),
      recourseLevel: new FormControl<string>(collateral.recourseLevel),
      guarantorBurndown: new FormControl<string>(collateral.guarantorBurndown),
      // Sub-facility specific fields
      name: new FormControl<string>('', Validators.required),
      productType: new FormControl<string>('', Validators.required),
      rateType: new FormControl<string>('', Validators.required),
      amount: new FormControl<number | null>(null, [
        Validators.required,
        Validators.min(0),
      ]),
      term: new FormControl<number | null>(null, [
        Validators.required,
        Validators.min(1),
      ]),
      amortization: new FormControl<number | null>(null, [Validators.min(0)]),
      interestOnlyPeriod: new FormControl<number | null>(null),
      closedDate: new FormControl<Date | null>(null, Validators.required),
      maturityDate: new FormControl<Date | null>(null, Validators.required),
      prepayment: new FormControl<string>(''),
      expectedUtilization: new FormControl<string>(''),
      fixedRate: new FormControl<number | null>(null),
      index: new FormControl<string>(''),
      spread: new FormControl<number | null>(null),
      rateFloorType: new FormControl<string>(''),
      floor: new FormControl<number | null>(null),
    });

    form
      .get('productType')
      .valueChanges.pipe(
        startWith(form.get('productType')?.value),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(value => {
        const amortizationCtrl = form.get('amortization');
        if (NO_AMORTIZATION_PATTERN.test(value)) {
          amortizationCtrl.clearValidators();
          amortizationCtrl.setValue(null, { emitEvent: false });
        } else {
          amortizationCtrl.setValidators([
            Validators.required,
            Validators.min(0),
          ]);
        }
        amortizationCtrl.updateValueAndValidity({ emitEvent: false });
      });

    return form;
  }

  isSubFloatingRate(form: FormGroup): boolean {
    return form.get('rateType')?.value === 'Floating';
  }

  isSubTermLoan(form: FormGroup): boolean {
    return /term/i.test(form.get('productType')?.value ?? '');
  }

  showSubAmortization(form: FormGroup): boolean {
    return !NO_AMORTIZATION_PATTERN.test(form.get('productType')?.value ?? '');
  }

  showSubFloorRate(form: FormGroup): boolean {
    const ft = form.get('rateFloorType')?.value;
    return !!ft && ft !== 'No Floor';
  }

  autoCalcSubMaturity(form: FormGroup): void {
    const closedDate = form.get('closedDate')?.value;
    const term = form.get('term')?.value;
    if (closedDate && term) {
      const maturity = new Date(closedDate);
      maturity.setMonth(maturity.getMonth() + term);
      form.get('maturityDate')?.setValue(maturity, { emitEvent: false });
    }
  }

  // ─── Draft Persistence ────────────────────────────────────

  private loadDraft(): void {
    this.stepperDataStore.loadDraft(STEPPER_NAME);
  }

  private restoreForms(data: Partial<ClosedDealFormValue>): void {
    if (data.setup) {
      this.setupForm.patchValue(data.setup);
    }
    if (data.structure) {
      this.structureForm.patchValue({
        ...data.structure,
        closedDate: data.structure.closedDate
          ? new Date(data.structure.closedDate)
          : null,
        maturityDate: data.structure.maturityDate
          ? new Date(data.structure.maturityDate)
          : null,
      });
    }
    if (data.borrower) {
      this.borrowerForm.patchValue(data.borrower);
    }
    if (data.pricing) {
      this.pricingForm.patchValue(data.pricing);
    }
    if (data.collateral) {
      this.collateralForm.patchValue(data.collateral);
    }
    if (data.cre) {
      this.creForm.patchValue(data.cre);
    }
    if (data.subFacilities?.length) {
      const forms = data.subFacilities.map((sf, i) => {
        const form = this.createSubFacilityForm();
        form.patchValue({
          ...sf,
          closedDate: sf.closedDate ? new Date(sf.closedDate) : null,
          maturityDate: sf.maturityDate ? new Date(sf.maturityDate) : null,
        });
        return form;
      });
      this.subFacilityForms.set(forms);
    }
    if (data.nda) {
      this.ndaForm.patchValue(data.nda);
    }
  }

  private tryRestoreStep(): void {
    if (!this.stepperReady || this.stepToRestore === null) return;

    const target = this.stepToRestore;
    this.stepToRestore = null;

    setTimeout(() => {
      this.stepper.linear = false;
      this.stepper.selectedIndex = target;
      this.stepper.linear = true;
      this.currentMainStep.set(target);
      this.updateConnectorLines(target);
    });
  }

  private buildDraftPayload(step: number): StepperDataPayload {
    return {
      stepperName: STEPPER_NAME,
      step,
      jsonData: JSON.stringify({
        setup: this.setupForm.getRawValue(),
        structure: this.structureForm.getRawValue(),
        borrower: this.borrowerForm.getRawValue(),
        pricing: this.pricingForm.getRawValue(),
        collateral: this.collateralForm.getRawValue(),
        cre: this.creForm.getRawValue(),
        subFacilities: this.subFacilityForms().map(f => f.getRawValue()),
        nda: this.ndaForm.getRawValue(),
        _savedAt: Date.now(),
      }),
    };
  }

  private saveDraft(step: number): void {
    this.stepperDataStore.saveDraft(this.buildDraftPayload(step));
  }

  // ─── Helpers ──────────────────────────────────────────────

  private updateCreVisibility(): void {
    const productType = this.structureForm.get('productType')?.value ?? '';
    const loanPurpose = this.setupForm.get('loanPurpose')?.value ?? '';
    const show =
      CRE_PRODUCT_TYPES_PATTERN.test(productType) ||
      CRE_LOAN_PURPOSE_PATTERN.test(loanPurpose);
    this.showCreStep.set(show);
  }

  private updatePricingValidators(isFloating: boolean): void {
    const fixedRateCtrl = this.pricingForm.get('fixedRate');
    const spreadCtrl = this.pricingForm.get('spread');
    const indexCtrl = this.pricingForm.get('index');

    if (isFloating) {
      fixedRateCtrl?.clearValidators();
      spreadCtrl?.setValidators([Validators.required]);
      indexCtrl?.setValidators([Validators.required]);
    } else {
      fixedRateCtrl?.setValidators([Validators.required, Validators.min(0)]);
      spreadCtrl?.clearValidators();
      indexCtrl?.clearValidators();
    }

    fixedRateCtrl?.updateValueAndValidity();
    spreadCtrl?.updateValueAndValidity();
    indexCtrl?.updateValueAndValidity();
  }

  private formatDate(d: Date | null): string | null {
    return d?.toISOString().split('T')[0] ?? null;
  }

  private formatPercentage(value: number | null): number | null {
    if (value == null) return null;
    return value / 100;
  }

  // Helper to get display label for schema IDs in sub-facility review
  getSchemaLabel(
    obs$: Observable<SchemaEnumItem[]>,
    id: string
  ): Observable<string> {
    if (!id) return of('N/A');
    return obs$.pipe(
      map(items => {
        const item = items.find(i => i.id === id);
        return item?.description ?? item?.label ?? id;
      })
    );
  }

  // ─── Build Item Payload ───────────────────────────────────

  private buildItemPayload(overrides?: {
    name?: string;
    amount?: number | null;
    productType?: string;
    rateType?: string;
    term?: number | null;
    amortization?: number | null;
    interestOnlyPeriod?: number | null;
    closedDate?: Date | null;
    maturityDate?: Date | null;
    prepayment?: string;
    expectedUtilization?: string;
    fixedRate?: number | null;
    index?: string;
    spread?: number | null;
    rateFloorType?: string;
    floor?: number | null;
    // Inherited overridable fields
    transactionType?: string;
    dealName?: string;
    numLenders?: number;
    loanPurpose?: string;
    industry?: string;
    revenueSize?: string;
    peSponsored?: string;
    usState?: string;
    recourseLevel?: string;
    guarantorBurndown?: string;
  }): Item {
    const setup = this.setupForm.getRawValue();
    const structure = this.structureForm.getRawValue();
    const borrower = this.borrowerForm.getRawValue();
    const pricing = this.pricingForm.getRawValue();
    const collateral = this.collateralForm.getRawValue();
    const cre = this.creForm.getRawValue();
    const nda = this.ndaForm.getRawValue();

    const itemName = overrides?.name ?? setup.dealName;
    const amount = overrides?.amount ?? structure.amount;
    const productType = overrides?.productType ?? structure.productType;
    const rateType = overrides?.rateType ?? structure.rateType;
    const term = overrides?.term ?? structure.term;
    const amortization = overrides?.amortization ?? structure.amortization;
    const interestOnlyPeriod =
      overrides?.interestOnlyPeriod ?? structure.interestOnlyPeriod;
    const closedDate = overrides?.closedDate ?? structure.closedDate;
    const maturityDate = overrides?.maturityDate ?? structure.maturityDate;
    const prepayment = overrides?.prepayment ?? pricing.prepayment;
    const expectedUtilization =
      overrides?.expectedUtilization ?? pricing.expectedUtilization;
    const fixedRate = overrides?.fixedRate ?? pricing.fixedRate;
    const index = overrides?.index ?? pricing.index;
    const spread = overrides?.spread ?? pricing.spread;
    const rateFloorType = overrides?.rateFloorType ?? pricing.rateFloorType;
    const floor = overrides?.floor ?? pricing.floor;

    const ndaTplId = nda.template?._id;

    const item: Item = {};
    item.name = itemName;
    item.stage = ItemStage.CLOSED;
    item.productType = ItemTypeBitMasks.LOAN;
    // FE NdaWorkflow control values are lowercase ('none'/'personal'); v2
    // ItemNdaWorkflow is the uppercase enum name — names align, so upper-case.
    item.ndaWorkflow = nda.workflow?.toUpperCase() as ItemNdaWorkflow;
    item.ndaTemplate = ndaTplId;
    item.myPar = {
      participationStage: ParticipationStage.INTERESTED,
      role: ParticipationRole.DEAL_ADMIN,
      committedQuantity: { a: amount ?? 0, c: 'USD' },
    };
    item.itemData = {
      shortIdMap: {
        a: SID_AGENCY,
        c: SID_COMMON,
        f: SID_FEES,
        b: SID_BORROWER,
        col: SID_COLLATERAL,
        g: SID_GUARANTORS,
        l: SID_PRODUCT_TERM,
        s: productType,
        i: overrides?.industry ?? borrower.industry,
        p: SID_PARTICIPATION,
      },
      data: {
        // Setup (overridable per sub-facility)
        'c.sTp': overrides?.transactionType ?? setup.transactionType,
        'c.loanPurpose': overrides?.loanPurpose ?? setup.loanPurpose,
        'c.numberOfLenders': overrides?.numLenders ?? setup.numLenders,
        // Structure
        'c.pr': productType,
        'c.oAmt': amount,
        'c.trm': term,
        'c.amtz': amortization,
        ...(interestOnlyPeriod ? { 'l.iop': interestOnlyPeriod } : {}),
        'c.cDt': this.formatDate(closedDate),
        'c.maturityDt': this.formatDate(maturityDate),
        'f.interestRateType': rateType,
        // Borrower (overridable per sub-facility)
        'c.indu': overrides?.industry ?? borrower.industry,
        'c.usState': (overrides?.usState ?? borrower.usState) || null,
        'b.borrowerRevenueSize': overrides?.revenueSize ?? borrower.revenueSize,
        'b.privateEquitySponsored':
          overrides?.peSponsored ?? borrower.peSponsored,
        // Pricing
        'c.sprd': this.formatPercentage(spread),
        'c.initInrRte': this.formatPercentage(fixedRate),
        'c.indx': index,
        'f.prepaymentPenaltyFee': prepayment,
        'c.expectedUtilization': expectedUtilization || null,
        'f.interestRateFloorType': rateFloorType || null,
        'f.whatIsTheFloorRate': this.formatPercentage(floor),
        'f.origFee': this.formatPercentage(pricing.originationFee),
        'f.performanceBasedPricing': pricing.performancePricing,
        // Collateral (overridable per sub-facility)
        'col.recourseLevel':
          overrides?.recourseLevel ?? collateral.recourseLevel,
        'g.guarantorLiabilityBurndown':
          overrides?.guarantorBurndown ?? collateral.guarantorBurndown,
        'f.interestRateHedge': collateral.hedge || null,
        // CRE
        'c.crePropertyClass': cre.propertyClass,
        'c.projectType': cre.projectType,
        // LTV/LTC/debtYield: form stores whole-number percentage (e.g. 34 for 34%),
        // but the collateral schema defines them as `format: percentage` expecting
        // decimals (e.g. 0.34). Convert here. Also: these fields live on the
        // collateral schema ('col' alias), not on product-term ('l') or common ('c').
        'col.loanToValue': this.formatPercentage(cre.ltv),
        'col.loanToCost': this.formatPercentage(cre.ltc),
        'col.debtYield': this.formatPercentage(cre.debtYield),
      },
    } as unknown as ItemData;

    return item;
  }

  // ─── Sub-stepper Navigation ────────────────────────────────

  enterSubStepper(): void {
    // Pre-fill inherited fields from parent into all sub-facility forms
    for (const form of this.subFacilityForms()) {
      this.prefillInheritedFields(form);
    }

    this.subStepperEntered.set(true);
    this.currentSubFacility.set(0);
    this.currentSubStep.set(0);
  }

  /** Navigate back to the step that hosts the sub-stepper (CRE when enabled). */
  returnToSubStepper(): void {
    if (!this.stepper) return;
    this.stepper.selectedIndex = this.stepper.steps.length - 1;
  }

  /** Pre-fill inherited fields from parent forms into a sub-facility form (only if empty). */
  private prefillInheritedFields(form: FormGroup | null): void {
    if (!form) return;
    const setup = this.setupForm.getRawValue();
    const borrower = this.borrowerForm.getRawValue();
    const collateral = this.collateralForm.getRawValue();

    const inherited: Record<string, any> = {
      transactionType: setup.transactionType,
      name: setup.dealName,
      numLenders: setup.numLenders,
      loanPurpose: setup.loanPurpose,
      industry: borrower.industry,
      revenueSize: borrower.revenueSize,
      peSponsored: borrower.peSponsored,
      usState: borrower.usState,
      recourseLevel: collateral.recourseLevel,
      guarantorBurndown: collateral.guarantorBurndown,
    };
    // Only patch if field is still at default (don't overwrite user edits)
    Object.entries(inherited).forEach(([key, val]) => {
      const ctrl = form.get(key);
      if (ctrl && !ctrl.value) {
        ctrl.setValue(val, { emitEvent: false });
      }
    });
  }

  subStepNext(): void {
    if (!this.validateCurrentSubStep()) return;

    const step = this.currentSubStep();
    const subIdx = this.currentSubFacility();
    const totalSubs = this.subFacilityForms().length;

    if (step < 2) {
      this.currentSubStep.set(step + 1);
    } else if (subIdx < totalSubs - 1) {
      // Moving to next sub-facility: sync inherited edits to parent,
      // then pre-fill next sub-facility with updated parent values
      this.syncInheritedFieldsToParent();
      this.currentSubFacility.set(subIdx + 1);
      this.currentSubStep.set(0);
      this.prefillInheritedFields(this.currentSubForm());
    }
    this.saveDraft(this.stepper?.selectedIndex ?? 6);
  }

  /** Marks dirty too — ShowOnDirtyErrorStateMatcher needs dirty, not just touched, to show errors. */
  private validateCurrentSubStep(): boolean {
    const form = this.currentSubForm();
    if (!form) return true;

    let valid = true;
    for (const directive of this.renderedControlNames()) {
      const ctrl = directive.control;
      if (ctrl.parent !== form) continue; // skip fields from other forms
      if (ctrl.invalid) {
        ctrl.markAsDirty();
        ctrl.markAsTouched();
        valid = false;
      }
    }
    return valid;
  }

  subStepPrev(): void {
    const step = this.currentSubStep();
    const subIdx = this.currentSubFacility();

    if (step > 0) {
      this.currentSubStep.set(step - 1);
    } else if (subIdx > 0) {
      this.currentSubFacility.set(subIdx - 1);
      this.currentSubStep.set(2);
    }
  }

  get isLastSubStep(): boolean {
    return (
      this.currentSubFacility() === this.subFacilityForms().length - 1 &&
      this.currentSubStep() === 2
    );
  }

  currentSubForm = computed(
    () => this.subFacilityForms()[this.currentSubFacility()] ?? null
  );

  /**
   * Sync inherited field edits from sub-facility review steps back to
   * the parent forms so the parent deal payload reflects any changes
   * the user made while reviewing sub-facilities.
   */
  private syncInheritedFieldsToParent(): void {
    const form = this.currentSubForm();
    if (!form) return;

    // Use the current sub-facility's inherited values
    const last = form.getRawValue();

    this.setupForm.patchValue(
      {
        transactionType: last.transactionType,
        numLenders: last.numLenders,
        loanPurpose: last.loanPurpose,
      },
      { emitEvent: false }
    );

    this.borrowerForm.patchValue(
      {
        industry: last.industry,
        revenueSize: last.revenueSize,
        peSponsored: last.peSponsored,
        usState: last.usState,
      },
      { emitEvent: false }
    );

    this.collateralForm.patchValue(
      {
        recourseLevel: last.recourseLevel,
        guarantorBurndown: last.guarantorBurndown,
      },
      { emitEvent: false }
    );
  }

  // ─── Actions ──────────────────────────────────────────────

  onCancel(): void {
    this.dialogRef.close();
  }

  onSubmit(): void {
    if (!this.isFormValid()) return;

    this.isSaving.set(true);

    const subForms = this.subFacilityForms();

    if (subForms.length > 0) {
      // Sync any review-step edits back to parent forms before building payload
      this.syncInheritedFieldsToParent();

      // Bulk upsert: parent with nested subItems
      const parentDeal = this.buildItemPayload();
      // SD-2150: link the uploaded source doc to the new deal (parent only).
      if (this.dialogData?.docId) {
        parentDeal.docId = this.dialogData.docId;
      }
      (parentDeal as any).subItems = subForms.map(form => {
        const sf = form.getRawValue();
        return this.buildItemPayload({
          // Inherited (editable per sub-facility)
          transactionType: sf.transactionType,
          dealName: sf.dealName,
          numLenders: sf.numLenders,
          loanPurpose: sf.loanPurpose,
          industry: sf.industry,
          revenueSize: sf.revenueSize,
          peSponsored: sf.peSponsored,
          usState: sf.usState,
          recourseLevel: sf.recourseLevel,
          guarantorBurndown: sf.guarantorBurndown,
          // Sub-facility specific
          name: sf.name,
          productType: sf.productType,
          rateType: sf.rateType,
          amount: sf.amount,
          term: sf.term,
          amortization: sf.amortization,
          interestOnlyPeriod: sf.interestOnlyPeriod,
          closedDate: sf.closedDate,
          maturityDate: sf.maturityDate,
          prepayment: sf.prepayment,
          expectedUtilization: sf.expectedUtilization,
          fixedRate: sf.fixedRate,
          index: sf.index,
          spread: sf.spread,
          rateFloorType: sf.rateFloorType,
          floor: sf.floor,
        });
      });

      // The bulk endpoint saves the whole tree in one transaction and links each
      // subItem to its saved parent, so `res[0]` is the parent deal.
      this.itemStore.bulkCreate$
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe(res => {
          if (this.stepperDataStore.draftExists()) {
            this.stepperDataStore.deleteDraft();
          }
          this.dialogRef.close();
          this.isSaving.set(false);
          const parentId = res?.[0]?._id;
          if (parentId) {
            this.router.navigate(['itms/agency', 'deal', btoa(parentId)], {
              queryParams: { prevUrl: this.router.url },
            });
          }
        });

      this.itemStore.bulkCreateFailure$
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.isSaving.set(false));

      this.itemStore.bulkCreate([parentDeal]);
    } else {
      const item = this.buildItemPayload();
      // SD-2150: link the uploaded source doc to the new deal.
      if (this.dialogData?.docId) {
        item.docId = this.dialogData.docId;
      }
      const dealName = this.setupForm.getRawValue().dealName;

      this.agencyStore.itemCreated$
        .pipe(
          filter(
            ({ res, mysid }) =>
              mysid === CREATE_DEAL_MYSID && res.name === dealName
          ),
          take(1),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(({ res }) => {
          this.agencyStore.disconnect(CREATE_DEAL_MYSID);
          if (this.stepperDataStore.draftExists()) {
            this.stepperDataStore.deleteDraft();
          }
          this.dialogRef.close();
          this.isSaving.set(false);
          this.router.navigate(['itms/agency', 'deal', btoa(res._id ?? '')], {
            queryParams: { prevUrl: this.router.url },
          });
        });

      this.agencyStore.itemCreateFailed$
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.isSaving.set(false));

      this.agencyStore.createItem(item);
    }
  }

  isFormValid(): boolean {
    const mainForms: FormGroup[] = [
      this.setupForm,
      this.structureForm,
      this.borrowerForm,
      this.pricingForm,
      this.collateralForm,
      this.ndaForm,
    ];

    if (this.showCreStep()) {
      mainForms.push(this.creForm);
    }

    for (const form of mainForms) {
      if (!form.valid) {
        form.markAllAsDirty();
        form.markAllAsTouched();
        return false;
      }
    }

    for (const sf of this.subFacilityForms()) {
      if (!sf.valid) {
        sf.markAllAsDirty();
        sf.markAllAsTouched();
        return false;
      }
    }

    return true;
  }
}
